import { ChangeEvent, Dispatch, SetStateAction } from "react";

import { useFormContext } from "react-hook-form";
import { Box, Button, TextField, Typography } from "@mui/material";
import ImageDialog from "@app/components/ImageDialog";
import RenderNewUploadingImagesList from "@app/components/RenderNewUploadingImagesList";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useRenderFileViewHook } from "@app/hooks/imagesRender";
import { ApiEndpoint, extendedFileFormats } from "@constants/index";
import { useDeleteRecipeInfoMutation } from "@modules/recipe/api/recipe.api";
import COLORS from "@app/colors";
import TextareaEditor from "common/Editor";

import { IFlowProcess } from "@pages/RecipeMutation/useCreateRecipeLogic";
import { CreateRecipeState, ICreateRecipeFormData, IRecipeInstructions } from "@pages/RecipeMutation/helper/types";
import { MainLayer, ContentWrapper, VisuallyHiddenInput } from "./styled";
import { recipeFirstKey } from "@pages/RecipeMutation/helper/constant";

interface MultipleInstructionsProps {
  id: string;
  index: number;
  isImageViewOpen: boolean;
  imageSrcForAction: string;
  isDeleteModalOpen: boolean;
  handleDeleteImage: (str: string) => void;
  setFlowProcess: Dispatch<SetStateAction<IFlowProcess>>;
  onChangeMultipleUpload: (event: ChangeEvent<HTMLInputElement>, index: number) => void;
}

const MultipleInstructions = ({
  id: instructionId,
  index,
  isImageViewOpen,
  imageSrcForAction,
  handleDeleteImage,
  setFlowProcess,
  onChangeMultipleUpload,
}: MultipleInstructionsProps) => {
  const { setValue, watch, register, formState: { errors } } = useFormContext<ICreateRecipeFormData>()
  const [deleteRecipeInfo] = useDeleteRecipeInfoMutation()
  const instructions = watch(recipeFirstKey)
  const key = `${recipeFirstKey}[${index}]` as `instructions.${number}`
  const image = watch(`${key}.uploadedFiles`)
  const error = errors?.[recipeFirstKey]?.[index]

  const isUploadedImage = image && 'url' in image

  const foundIstructionImages = instructions.find(({ id }) => id === instructionId) || {} as IRecipeInstructions
  const previews = useRenderFileViewHook(image, foundIstructionImages?.existingImages)

  const requestToDeleteRecipeInfo = async () => {
    try {
      if (instructionId) {
        await deleteRecipeInfo({ id: instructionId })
        setValue(recipeFirstKey, [...instructions.filter((item) => item.id !== instructionId)])

        return
      }

      const newArray = instructions.filter((_, i) => i !== index)

      setValue(recipeFirstKey, newArray)
    } catch (e) {
      console.log('Error:', e);
    }
  }

  return (
    <MainLayer>
      <Typography
        component="h5"
        variant="h5"
        sx={{ mb: 1 }}
      >
        Instrution
        {' '}
        {index + 1}
      </Typography>
      <ContentWrapper>
        <TextField
          label="title Am"
          {...register(`${key}.titleAm`)}
          error={!!error?.titleAm}
        />
        <TextField
          label="title En"
          {...register(`${key}.titleEn`)}
          error={!!error?.titleEn}
        />
        <TextField
          label="title Ru"
          {...register(`${key}.titleRu`)}
          error={!!error?.titleRu}
        />

        <TextareaEditor
          value={watch(`${key}.descriptionAm`)}
          label="Description Am"
          name={`${key}.descriptionAm`}
          setValue={setValue}
          error={errors?.descriptionAm?.message}
        />

        <TextareaEditor
          value={watch(`${key}.descriptionEn`)}
          label="Description En"
          name={`${key}.descriptionEn`}
          setValue={setValue}
          error={errors?.descriptionEn?.message}
        />

        <TextareaEditor
          value={watch(`${key}.descriptionRu`)}
          label="Description Ru"
          name={`${key}.descriptionRu`}
          setValue={setValue}
          error={errors?.descriptionRu?.message}
        />
      </ContentWrapper>
      <RenderNewUploadingImagesList<CreateRecipeState>
        setState={setFlowProcess}
        handleDeleteImage={handleDeleteImage}
        newPreviewsList={isUploadedImage ? [`${ApiEndpoint}/${image.url}`] : previews}
        togetherImagesLength={isUploadedImage ? 1 : previews.length}
      />
      <ImageDialog
        url={imageSrcForAction}
        isOpen={isImageViewOpen}
        onClose={() => {
          setFlowProcess((prev) => ({ ...prev, isImageViewOpen: false }))
        }}
      />
      <Typography sx={{ color: COLORS.red }}>{errors?.[recipeFirstKey]?.[index]?.images?.message}</Typography>
      <Box
        sx={{ mt: 1, display: 'flex', gap: 1 }}
      >
        <div>
          <Button
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
          >
            <p>{ image ? 'Edit Image' : 'Upload Pictures' }</p>
            <VisuallyHiddenInput
              type="file"
              multiple
              id="files"
              name="images"
              accept={extendedFileFormats.join(',')}
              onChange={(e) => onChangeMultipleUpload(e, index)}
            />
          </Button>
          <Typography sx={{ color: COLORS.red }}>
            {errors?.[recipeFirstKey]?.[index]?.uploadedFiles?.message}
          </Typography>
        </div>
        <Button
          sx={{ height: 'min-content' }}
          variant="contained"
          onClick={() => requestToDeleteRecipeInfo()}
        >
          Delete Instraction
        </Button>
      </Box>
    </MainLayer>
  )
}

export default MultipleInstructions
